// The frame of the pages with a side panel: the panel (390px) on the left,
// the white panel on the right, as in the frame «UltimeNews» of the design.
// The panel closes from the chevron of its title row (PanelToggle, drawn by
// PanelTitle) and opens from the strip that takes its place. Whether it is
// open is the session's own state (src/app/sidePanel.ts, held by Providers):
// closed when the app opens, then as the user left it on every page until
// the page is reloaded; nothing is stored. Since 20/10/2026 the panel stays
// in view while the page scrolls (sticky): open, on wide screens, where it
// stands beside the cards; closed, its strip, at every width.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSidePanel } from "../app/sidePanel";
import { Icon } from "./Icon";

/** The side panel of a page: its title, which names the chevron that opens it, and how it is drawn. */
export type SidePanel = {
  title: string;
  /** Draws the panel; `collapse` closes it (the chevron of PanelTitle). */
  render: (collapse: () => void) => ReactNode;
};

/** The names of the chevron, after the panel it closes or opens. */
const closePanelLabel = (title: string) => `Chiudi il pannello «${title}»`;
const openPanelLabel = (title: string) => `Apri il pannello «${title}»`;

type PanelToggleProps = {
  title: string;
  /** True in the strip of the closed panel: the chevron points right and opens it. */
  collapsed?: boolean;
  onPress: () => void;
};

/** The chevron that closes the panel (in its title row) or opens it (in the strip). */
export function PanelToggle({ title, collapsed = false, onPress }: PanelToggleProps) {
  return (
    <button
      type="button"
      data-panel-toggle=""
      onClick={onPress}
      aria-expanded={!collapsed}
      aria-label={collapsed ? openPanelLabel(title) : closePanelLabel(title)}
      className="cursor-pointer rounded-sm"
    >
      <Icon name="chevronUp" className={collapsed ? "rotate-90" : "-rotate-90"} />
    </button>
  );
}

type NewsLayoutProps = {
  sidebar: SidePanel;
  /** The id of the element that names the main (the title of a record read with its index). */
  labelledBy?: string;
  children: ReactNode;
};

/** Colonna laterale (390px) + pannello bianco con le news, come nel frame "UltimeNews". */
export function NewsLayout({ sidebar, labelledBy, children }: NewsLayoutProps) {
  const { open, setOpen } = useSidePanel();
  // the presses on the chevron: after each one, the chevron that took the pressed one's place gets the focus
  const [presses, setPresses] = useState(0);
  const aside = useRef<HTMLElement>(null);

  useEffect(() => {
    if (presses === 0) return;
    aside.current?.querySelector<HTMLElement>("[data-panel-toggle]")?.focus();
  }, [presses]);

  const toggle = (next: boolean) => {
    setOpen(next);
    setPresses((n) => n + 1);
  };

  if (!open) {
    return (
      <div className="mt-2.5 flex flex-1 gap-[27px]">
        {/* sticky: the panel opens again from anywhere down the page */}
        <aside ref={aside} className="sticky top-0 flex h-fit items-center gap-2.5 self-start pt-2">
          <Icon name="sliders" />
          <PanelToggle title={sidebar.title} collapsed onPress={() => toggle(true)} />
        </aside>
        <main
          aria-labelledby={labelledBy}
          className="flex min-w-0 flex-1 justify-center overflow-x-clip bg-white px-6 pt-2 pb-12 lg:px-[93px]"
        >
          <div className="flex w-full max-w-[1063px] flex-col gap-[49px]">{children}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="mt-2.5 flex flex-1 flex-col gap-8 lg:flex-row lg:gap-[37px]">
      {/*
        Beside the cards (lg), the panel stays in view while the page scrolls: self-start, or it would be as tall
        as the row; taller than the window, it scrolls by itself (the design's thin bar), without handing the
        scroll to the page. The 12px on each side (-mx-3 px-3, 414 = 390 + 24) keep inside the box what the
        overflow would cut: the bar of ScrollArea (-mr-3) and the focus rings. Above the cards (under lg), as before.
      */}
      <aside
        ref={aside}
        className="scroll-fonti w-full shrink-0 pt-2 lg:sticky lg:top-0 lg:-mx-3 lg:max-h-dvh lg:w-[414px] lg:self-start lg:overflow-y-auto lg:overscroll-contain lg:px-3 lg:pb-4"
      >
        {sidebar.render(() => toggle(false))}
      </aside>
      <main aria-labelledby={labelledBy} className="min-w-0 flex-1 overflow-x-clip bg-white px-[19px] pt-2 pb-12">
        <div className="flex flex-col gap-[49px]">{children}</div>
      </main>
    </div>
  );
}
